// ── <ResolutionMomentPicker> — the "Resolves" line in the Check your question panel ──
//
// Always shows the exact moment the question resolves — date, time, zone — in the
// asker's own clock first, so a wrong guess is visible before it is forecast.
// Design: experimental/sooth-agents/docs/2026-09-21-ask-to-clarify-design.md.
//
// Two modes:
//   • awaiting — the server found readings that could change the answer, or the
//     event has no confirmed date; the forecast is HELD until the asker picks.
//   • settled  — the moment is shown with ✎ Edit, which offers the same options
//     (if any) plus a free date/time in the asker's clock.
//
// Options are numbered cards; pressing 1–N picks one, and the "your own date" card
// (always last) takes the next number and focuses its field.
//
// Pure props: `onChoose({moment_utc, zone, label})` applies a moment; the panel
// owns what that does (start the held forecast, or rerun a running one).
// `onEditStart()` fires when ✎ Edit opens, so the panel can pause its countdown.
// `renderAction(applyPending, pendingInvalid)` draws the panel's buttons in the bottom row:
// `applyPending` applies a typed-but-unapplied date (null when there is none), and
// `pendingInvalid` marks a changed date that does not parse, which Confirm must not drop.
// `children` render between the moment and that row (the panel's resolution criteria).

import { useEffect, useRef, useState } from "react";
import { C } from "../lib/colors.js";
import { Button } from "./ui/Primitives.jsx";
import { IconCalendar } from "./ui/Icons.jsx";
import {
  askerZone,
  cardForKey,
  formatMomentInZone,
  fromLocalInputValue,
  resolutionOptionsOf,
  resolutionSummary,
  toLocalInputValue,
  unconfirmedEventOf,
} from "../lib/forecast/resolutionChoice.js";
import { ui } from "../lib/telemetry/instrument.js";

const MONO = '"IBM Plex Mono",monospace';

const cardStyle = (highlight) => ({
  display: "flex",
  alignItems: "center",
  gap: 12,
  width: "100%",
  boxSizing: "border-box",
  textAlign: "left",
  background: C.rowInactive,
  color: C.white,
  border: `1px solid ${highlight ? C.accent : C.border}`,
  borderRadius: 8,
  padding: "10px 12px",
  fontSize: 13,
  lineHeight: 1.45,
  letterSpacing: 0,
  font: "inherit",
});

/** The keyboard number shown at the left of every card. */
function NumberBadge({ n }) {
  return (
    <span
      aria-hidden="true"
      className="sy-num"
      style={{
        flex: "none",
        width: 22,
        height: 22,
        borderRadius: 6,
        border: `1px solid ${C.border}`,
        color: C.grayL,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 12,
      }}
    >
      {n}
    </span>
  );
}

function OptionCard({ n, opt, zone, onChoose, disabled, bestGuess }) {
  return (
    <button
      {...ui("resolution.option", n)}
      data-telemetry-redact=""
      type="button"
      disabled={disabled}
      onClick={() => onChoose(opt)}
      data-testid="resolution-option"
      aria-keyshortcuts={String(n)}
      style={{ ...cardStyle(bestGuess), cursor: disabled ? "wait" : "pointer" }}
    >
      <NumberBadge n={n} />
      <span style={{ display: "flex", flexDirection: "column" }}>
        <span style={{ color: C.white, fontSize: 15, fontWeight: 500 }}>
          {formatMomentInZone(opt.moment_utc, zone)}
        </span>
        <span style={{ color: C.gray, fontSize: 12 }}>
          {opt.label || "Our best reading"}
          {bestGuess && <span style={{ color: C.accent }}> · best guess</span>}
        </span>
      </span>
    </button>
  );
}

/**
 * The "your own date" card: same shape as an option, with a clearly editable field. The
 * panel's CONTINUE applies a typed date; Enter in the field does the same.
 */
function CustomCard({ n, inputRef, custom, setCustom, onApply }) {
  return (
    <div style={cardStyle(false)} data-testid="resolution-custom-card">
      <NumberBadge n={n} />
      <span style={{ color: C.white, whiteSpace: "nowrap" }}>Your own date</span>
      <input
        {...ui("resolution.custom-date")}
        ref={inputRef}
        type="datetime-local"
        value={custom}
        onChange={(e) => setCustom(e.target.value)}
        onKeyDown={(e) => {
          // The panel treats Enter as CONTINUE; here it means "apply this time".
          if (e.key === "Enter") {
            e.preventDefault();
            e.stopPropagation();
            onApply();
          }
        }}
        aria-label="Your own resolution date and time, in your time zone"
        data-testid="resolution-custom"
        style={{
          flex: 1,
          minWidth: 0,
          background: C.inputBg,
          color: C.white,
          border: `1px solid ${C.border}`,
          borderRadius: 6,
          padding: "6px 8px",
          fontFamily: MONO,
          fontSize: 12,
          colorScheme: "dark light",
        }}
      />
      <span style={{ fontSize: 12, color: C.gray, whiteSpace: "nowrap" }}>your time zone</span>
    </div>
  );
}

/** Whether a key event is aimed at a text field (so digits there are typing, not shortcuts). */
function typingInField(e) {
  const t = e.target;
  if (!t || typeof t !== "object") return false;
  const tag = (t.tagName || "").toLowerCase();
  return tag === "input" || tag === "textarea" || tag === "select" || t.isContentEditable;
}

/** The RESOLVES line, the numbered options, and the custom-date card. */
export default function ResolutionMomentPicker({
  question,
  awaiting,
  disabled,
  onChoose,
  onEditStart,
  renderAction = null,
  children = null,
}) {
  const zone = askerZone();
  const summary = resolutionSummary(question, zone);
  const options = resolutionOptionsOf(question);
  // The event this question waits on when it has no confirmed date — then the
  // question is "when", not "which reading", and the user can type a date too.
  const unconfirmed = unconfirmedEventOf(question);
  const [editing, setEditing] = useState(false);
  const [custom, setCustom] = useState(() => (summary ? toLocalInputValue(summary.iso) : ""));
  const customRef = useRef(null);

  const choose = (opt) => {
    setEditing(false);
    onChoose(opt);
  };
  const applyCustom = () => {
    const d = fromLocalInputValue(custom);
    if (!d) return;
    choose({ moment_utc: d.toISOString(), zone, label: "Set by you" });
  };

  // Which cards are showing: options while choosing or editing, and the custom card
  // when the date is unconfirmed (choosing) or in ✎ Edit.
  const choosing = awaiting || editing;
  const showCustom = editing || (awaiting && !!unconfirmed);
  const cardCount = options.length + (showCustom ? 1 : 0);
  // A date typed into the open editor that differs from the one shown and was never applied.
  const shownValue = summary ? toLocalInputValue(summary.iso) : "";
  const dirty = showCustom && !disabled && custom !== shownValue;
  const applyPending = dirty && fromLocalInputValue(custom) ? applyCustom : null;
  // A changed date that does not parse (e.g. cleared): CONTINUE waits rather than drop it.
  const pendingInvalid = dirty && !applyPending;
  const action = renderAction ? renderAction(applyPending, pendingInvalid) : null;

  // Number keys pick a card. Re-attached whenever what it acts on changes — a single
  // window listener, so re-binding per render is cheap and keeps it current.
  useEffect(() => {
    if (!choosing) return undefined;
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey || typingInField(e)) return;
      const card = disabled ? null : cardForKey(e.key, options.length, showCustom);
      if (!card) return;
      e.preventDefault();
      if (card.type === "option") {
        setEditing(false);
        onChoose(options[card.index]);
      } else customRef.current?.focus();
    };
    // telemetry-gesture: number keys pick a resolution card; resolution.apply records the pick
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [choosing, disabled, options, showCustom, onChoose]);

  const actionRow = action ? (
    <div
      style={{
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        gap: 8,
        marginTop: 28,
      }}
    >
      {action}
    </div>
  ) : null;

  // No moment to show: the panel's buttons still need their row.
  if (!summary) {
    return (
      <>
        {children}
        {actionRow}
      </>
    );
  }

  const cards = (
    <div style={{ display: "grid", gap: 8 }}>
      {options.map((opt, i) => (
        <OptionCard
          key={opt.moment_utc}
          n={i + 1}
          opt={opt}
          zone={zone}
          onChoose={choose}
          disabled={disabled}
          // "Best guess" only makes sense between readings; an unconfirmed date has none.
          bestGuess={i === 0 && !unconfirmed}
        />
      ))}
      {showCustom && (
        <CustomCard
          n={options.length + 1}
          inputRef={customRef}
          custom={custom}
          setCustom={setCustom}
          onApply={applyCustom}
        />
      )}
      {cardCount > 0 && (
        <div style={{ fontSize: 12, color: C.gray }}>
          Press {cardCount > 1 ? `1–${cardCount}` : "1"} to choose
        </div>
      )}
    </div>
  );

  return (
    <div data-testid="resolution-moment" style={{ marginTop: 20 }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "auto 1fr auto",
          alignItems: "start",
          columnGap: 12,
        }}
      >
        <span style={{ color: C.gray, display: "flex", paddingTop: 2 }}>
          <IconCalendar width={16} height={16} />
        </span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 12, color: C.gray, marginBottom: 2 }}>Resolves</div>
          {awaiting ? (
            <div style={{ fontSize: 13, color: C.white, lineHeight: 1.5 }}>
              {unconfirmed ? (
                <span data-testid="resolution-unconfirmed">
                  No confirmed date yet for {unconfirmed}. When should this question resolve? Pick
                  one or enter a date. The forecast starts when you choose.
                </span>
              ) : (
                <>
                  Which moment did you mean? Your question could resolve at different times, and the
                  answer could differ. Choose one to start the forecast.
                </>
              )}
            </div>
          ) : (
            <>
              <div style={{ color: C.white, fontSize: 15, fontWeight: 500, lineHeight: 1.4 }}>
                {summary.yours}
              </div>
              {(summary.theirs || summary.reason) && (
                <div style={{ color: C.gray, fontSize: 12, lineHeight: 1.5 }}>
                  {[summary.reason, summary.theirs].filter(Boolean).join(" · ")}
                </div>
              )}
              {summary.estimated && (
                <div
                  style={{ fontSize: 12, color: C.gray, marginTop: 2, lineHeight: 1.5 }}
                  data-testid="resolution-estimated"
                >
                  Estimated from the usual start time and length, with room for overruns. Change it
                  if you know the exact time.
                </div>
              )}
            </>
          )}
        </div>
        {!awaiting && !editing ? (
          <Button
            {...ui("resolution.edit")}
            variant="ghost"
            size="sm"
            disabled={disabled}
            onClick={() => {
              setEditing(true);
              onEditStart?.();
            }}
            data-testid="resolution-edit"
          >
            Change date
          </Button>
        ) : (
          <span />
        )}
      </div>
      {(awaiting || editing) && <div style={{ marginTop: 14 }}>{cards}</div>}
      {children}
      {actionRow}
    </div>
  );
}
